/**
 * Groups screen: group switcher tabs at top, then one group's detail.
 * Fetches GET /api/groups for the switcher and GET /api/groups/{id} for
 * the active group's detail (members + upcoming sessions).
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import {
  announceToGroup,
  createPoll,
  getGroupDetail,
  getGroupMessages,
  getMyGroups,
  listPolls,
  removeMember,
  rsvpSession,
  type ClubMessageDTO,
  type GroupDetailDTO,
  type GroupListItem,
  type GroupMemberDTO,
  type Me,
  type PollDTO,
  type SessionDTO,
} from "../api";
import { PollCard } from "../components/PollCard";
import { CourtAssignModal } from "../components/CourtAssignModal";
import { LadderSection } from "../components/LadderSection";
import { RecentlyPlayedCard } from "../components/RecentlyPlayedCard";
import { RecordResultModal } from "../components/RecordResultModal";
import { SessionResultsModal } from "../components/SessionResultsModal";
import { RsvpBreakdownModal } from "../components/RsvpBreakdownModal";
import { SessionChatModal } from "../components/SessionChatModal";
import { SessionRow } from "../components/SessionRow";

type ChatTarget = { id: string; title: string };

export function GroupsScreen({
  me,
  onAskAgent,
  activeGroupId,
  onSelectGroup,
  onCreateClub,
  onOpenSettings,
}: {
  me: Me;
  onAskAgent: (prefill?: string) => void;
  /** Active group from the URL (/groups/<id>), or null for /groups. */
  activeGroupId: string | null;
  /** Navigate to a group. `replace` swaps the URL without a history entry —
   *  used to canonicalize bare /groups → /groups/<first-id>. */
  onSelectGroup: (id: string, replace?: boolean) => void;
  onCreateClub: () => void;
  onOpenSettings: (groupId: string) => void;
}) {
  const [groups, setGroups] = useState<GroupListItem[] | null>(null);
  const [detail, setDetail] = useState<GroupDetailDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rsvpModalSession, setRsvpModalSession] = useState<SessionDTO | null>(null);
  const [chatTarget, setChatTarget] = useState<ChatTarget | null>(null);
  const [courtTarget, setCourtTarget] = useState<ChatTarget | null>(null);
  const [recordTarget, setRecordTarget] = useState<{
    sessionId: string;
    groupId: string;
    format: "singles" | "doubles";
  } | null>(null);
  const [resultsTarget, setResultsTarget] = useState<{
    sessionId: string;
    groupId: string;
  } | null>(null);

  // Active group is URL-driven; fall back to the first group for bare /groups.
  const activeId = activeGroupId ?? groups?.[0]?.id ?? null;

  // Load the group list once.
  useEffect(() => {
    let cancelled = false;
    getMyGroups()
      .then((r) => !cancelled && setGroups(r.groups))
      .catch((e) => !cancelled && setError(String(e?.message ?? e)));
    return () => { cancelled = true; };
  }, []);

  // Canonicalize bare /groups → /groups/<first-id> so the URL is always
  // precise (and shareable). Uses replace so it doesn't add a history entry.
  useEffect(() => {
    if (activeGroupId === null && groups && groups[0]) {
      onSelectGroup(groups[0].id, true);
    }
  }, [activeGroupId, groups, onSelectGroup]);

  // Load detail whenever the active group changes.
  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    setDetail(null);
    getGroupDetail(activeId)
      .then((d) => !cancelled && setDetail(d))
      .catch((e) => !cancelled && setError(String(e?.message ?? e)));
    return () => { cancelled = true; };
  }, [activeId]);

  async function refresh() {
    if (!activeId) return;
    const d = await getGroupDetail(activeId);
    setDetail(d);
  }

  async function handleRsvp(sessionId: string, intent: "yes" | "no") {
    try {
      await rsvpSession(sessionId, intent);
      await refresh();
    } catch (e) {
      setError(String((e as Error).message));
    }
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-5 pt-12">
        <div className="border-2 border-ink rounded-md p-6">
          <div className="font-display font-bold text-xl mb-2">Couldn't load groups.</div>
          <div className="text-ink/65 text-sm mb-3">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-ink text-bg font-display font-bold text-sm cs-tap"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (groups === null) return <GroupsSkeleton />;
  if (groups.length === 0) {
    return (
      <div className="max-w-5xl mx-auto px-5 pt-12">
        <div className="border-2 border-ink rounded-md p-8 text-center cs-reveal">
          <div className="font-display font-bold text-2xl mb-2">No clubs yet.</div>
          <div className="text-ink/65 mb-5">
            Create one to start scheduling, or paste an invite link a friend sent you.
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={onCreateClub}
              className="px-4 py-2 bg-ink text-bg font-display font-bold text-sm cs-tap"
            >
              Create a club
            </button>
            <button
              onClick={() => onAskAgent("Join a club with a code")}
              className="px-4 py-2 border-2 border-ink/20 font-display font-bold text-sm cs-tap"
            >
              Join with a code
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-5 pt-8">
      {/* Group switcher — horizontal scroll on narrow viewports.
          The right-edge fade hints there's more offscreen on mobile. */}
      <div className="relative cs-reveal mb-6">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 scroll-px-1">
          {groups.map((g) => (
            <button
              key={g.id}
              onClick={() => onSelectGroup(g.id)}
              className={
                "shrink-0 px-4 py-2 font-display font-bold text-sm cs-tap whitespace-nowrap " +
                (g.id === activeId
                  ? "bg-ink text-bg"
                  : "border border-ink/20 hover:border-ink/40")
              }
            >
              {g.name}
            </button>
          ))}
          <button
            onClick={onCreateClub}
            className="shrink-0 px-4 py-2 border border-ink/20 border-dashed text-ink/50 font-mono text-xs uppercase tracking-wider hover:border-ink/40 hover:text-ink/80 whitespace-nowrap"
          >
            + New club
          </button>
        </div>
        {/* Fade-out gradient — visible when scrollable content extends beyond
            the viewport. Pointer-events: none so it doesn't block clicks. */}
        <div className="pointer-events-none absolute top-0 right-0 bottom-1 w-8 bg-gradient-to-l from-bg to-transparent sm:hidden" />
      </div>

      {detail ? (
        <GroupDetail
          detail={detail}
          onAskAgent={onAskAgent}
          onRsvp={handleRsvp}
          onShowRsvps={setRsvpModalSession}
          onOpenChat={(id, title) => setChatTarget({ id, title })}
          onAssignCourts={(id, title) => setCourtTarget({ id, title })}
          onRecord={(id, format) =>
            setRecordTarget({ sessionId: id, groupId: detail.id, format })
          }
          onShowResults={(id) => setResultsTarget({ sessionId: id, groupId: detail.id })}
          onOpenSettings={() => onOpenSettings(detail.id)}
          onRefresh={refresh}
        />
      ) : (
        <DetailSkeleton />
      )}

      {rsvpModalSession && (
        <RsvpBreakdownModal
          session={rsvpModalSession}
          onClose={() => setRsvpModalSession(null)}
        />
      )}
      {chatTarget && (
        <SessionChatModal
          sessionId={chatTarget.id}
          title={chatTarget.title}
          meId={me.uid}
          onClose={() => setChatTarget(null)}
        />
      )}
      {courtTarget && (
        <CourtAssignModal
          sessionId={courtTarget.id}
          title={courtTarget.title}
          onClose={() => {
            setCourtTarget(null);
            void refresh();
          }}
        />
      )}
      {recordTarget && (
        <RecordResultModal
          groupId={recordTarget.groupId}
          sessionId={recordTarget.sessionId}
          defaultFormat={recordTarget.format}
          onClose={(recorded) => {
            setRecordTarget(null);
            if (recorded) void refresh();
          }}
        />
      )}
      {resultsTarget && (
        <SessionResultsModal
          groupId={resultsTarget.groupId}
          sessionId={resultsTarget.sessionId}
          onClose={() => setResultsTarget(null)}
        />
      )}
    </div>
  );
}

function GroupsSkeleton() {
  return (
    <div className="max-w-5xl mx-auto px-5 pt-8 cs-reveal">
      <div className="flex gap-2 mb-6">
        <div className="h-10 w-32 bg-ink/10 rounded" />
        <div className="h-10 w-32 bg-ink/10 rounded" />
      </div>
      <DetailSkeleton />
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="cs-reveal">
      <div className="h-10 w-60 bg-ink/10 rounded mb-2" />
      <div className="h-5 w-40 bg-ink/10 rounded mb-8" />
      <div className="h-16 w-full bg-ink/10 rounded-md mb-8" />
      <div className="h-6 w-32 bg-ink/10 rounded mb-3" />
      <div className="space-y-2 mb-8">
        <div className="h-20 w-full bg-ink/10 rounded-md" />
        <div className="h-20 w-full bg-ink/10 rounded-md" />
      </div>
    </div>
  );
}

function GroupDetail({
  detail,
  onAskAgent,
  onRsvp,
  onShowRsvps,
  onOpenChat,
  onAssignCourts,
  onRecord,
  onShowResults,
  onOpenSettings,
  onRefresh,
}: {
  detail: GroupDetailDTO;
  onAskAgent: (prefill?: string) => void;
  onRsvp: (sessionId: string, intent: "yes" | "no") => void;
  onShowRsvps: (s: SessionDTO) => void;
  onOpenChat: (sessionId: string, title: string) => void;
  onAssignCourts: (sessionId: string, title: string) => void;
  onRecord: (sessionId: string, format: "singles" | "doubles") => void;
  onShowResults: (sessionId: string) => void;
  onOpenSettings: () => void;
  onRefresh: () => void;
}) {
  const resultsVisible = detail.settings?.sessionResults !== false;
  const ladderVisible =
    detail.role === "admin" || detail.settings?.visibilityStandings === "public";
  const sections = [
    { id: "sec-schedule", label: "Schedule" },
    ...(detail.recently_played.length > 0
      ? [{ id: "sec-recent", label: "Recently played" }]
      : []),
    ...(ladderVisible ? [{ id: "sec-ladder", label: "Ladder" }] : []),
    { id: "sec-polls", label: "Polls" },
    { id: "sec-discussion", label: "Discussion" },
    { id: "sec-members", label: "Members" },
  ];
  function jump(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  const [announceOpen, setAnnounceOpen] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [memberError, setMemberError] = useState<string | null>(null);
  const adminCount = detail.members.filter((m) => m.role === "admin").length;

  async function handleRemoveMember(member: GroupMemberDTO) {
    if (!window.confirm(`Remove ${member.name} from ${detail.name}? This can't be undone.`)) return;
    setRemovingId(member.user_id);
    setMemberError(null);
    try {
      await removeMember(detail.id, member.user_id);
      onRefresh();
    } catch (e) {
      setMemberError(String((e as Error).message));
    } finally {
      setRemovingId(null);
    }
  }

  // Scroll-spy: highlight the section nearest the top so the nav reads as a
  // real menu showing where you are.
  const [activeSection, setActiveSection] = useState(sections[0]?.id ?? "");
  const sectionIds = sections.map((s) => s.id).join(",");
  useEffect(() => {
    const els = sectionIds
      .split(",")
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el != null);
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (!visible.length) return;
        const top = visible.reduce((a, b) =>
          a.boundingClientRect.top < b.boundingClientRect.top ? a : b,
        );
        setActiveSection(top.target.id);
      },
      { rootMargin: "-15% 0px -75% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sectionIds]);
  return (
    <>
      {announceOpen && (
        <AnnounceModal
          groupId={detail.id}
          groupName={detail.name}
          onClose={() => setAnnounceOpen(false)}
        />
      )}
      <div className="cs-reveal relative z-20 flex items-start justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55 mb-1">
            You're {detail.role === "admin" ? "an admin" : "a member"}
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tight">{detail.name}</h1>
          <div className="text-ink/55 mt-1">
            {detail.member_count} members · {detail.sport}
          </div>
        </div>
        {detail.role === "admin" && (
          <ManageMenu
            onMessage={() => setAnnounceOpen(true)}
            onSettings={onOpenSettings}
          />
        )}
      </div>

      <InviteLink code={detail.join_code} />

      {/* Two plain-language CTAs for the two things people do:
          - "Schedule a game" (admin) → a planned one-off session; the agent
            asks day/time, creates it, and invites the whole group to RSVP.
          - "Find a player" (any member) → a pickup beacon to skill-matched
            members. Both seed the chat agent. */}
      <div className="cs-reveal flex flex-col sm:flex-row gap-3 mb-8">
        {detail.role === "admin" && (
          <button
            onClick={() =>
              onAskAgent(`Schedule a one-off game in ${detail.name} and ask everyone to RSVP`)
            }
            className="flex-1 px-5 py-4 bg-accent text-ink rounded-md font-display font-bold text-lg cs-tap flex items-center justify-center gap-2"
          >
            Schedule a game
          </button>
        )}
        <button
          onClick={() =>
            onAskAgent(`Find me a player for a pickup game in ${detail.name}`)
          }
          className="flex-1 px-5 py-4 bg-ink text-bg rounded-md font-display font-bold text-lg cs-tap flex items-center justify-center gap-2"
        >
          Find a player to play
        </button>
      </div>

      {/* Sticky jump-to-section nav. Sits just under the app header (top-14). */}
      <nav className="sticky top-14 z-20 -mx-5 px-5 py-2.5 mb-6 bg-bg/95 backdrop-blur border-b border-ink/10">
        <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1">
          {sections.map((s) => {
            const active = activeSection === s.id;
            return (
              <button
                key={s.id}
                onClick={() => jump(s.id)}
                className={
                  "shrink-0 rounded-full px-3.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider cs-tap whitespace-nowrap transition " +
                  (active
                    ? "bg-ink text-bg"
                    : "bg-ink/5 text-ink/65 hover:bg-ink/10 hover:text-ink")
                }
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </nav>

      <div id="sec-schedule" className="cs-reveal mb-10 scroll-mt-28">
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display font-bold text-2xl">Schedule</h2>
        </div>
        {detail.sessions.length === 0 ? (
          <div className="border border-dashed border-ink/20 rounded-md p-6 text-center text-ink/55">
            No upcoming sessions.
            {detail.role === "admin" && (
              <button
                onClick={() => onAskAgent(`Schedule next four ${detail.sport} sessions in ${detail.name}`)}
                className="block mx-auto mt-3 px-4 py-2 bg-ink text-bg font-display font-bold text-sm cs-tap"
              >
                Set up the next four
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {detail.sessions.slice(0, 6).map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                isAdmin={detail.role === "admin"}
                onRsvp={onRsvp}
                onShowRsvps={() => onShowRsvps(s)}
                onOpenChat={() => onOpenChat(s.id, s.starts_at_display)}
                onAssignCourts={() => onAssignCourts(s.id, s.starts_at_display)}
                onRecord={() => s.format && onRecord(s.id, s.format)}
                onShowResults={() => onShowResults(s.id)}
                resultsVisible={resultsVisible}
              />
            ))}
            {detail.sessions.length > 6 && (
              <div className="text-center text-xs font-mono uppercase tracking-wider text-ink/45 pt-2">
                +{detail.sessions.length - 6} more upcoming
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recently played — most recent past session, so scores can be entered
          after the fact. Hidden when there's nothing in the last 14 days. */}
      {detail.recently_played.length > 0 && (
        <div id="sec-recent" className="cs-reveal mb-10 scroll-mt-28">
          <div className="flex items-end justify-between mb-3">
            <h2 className="font-display font-bold text-2xl">Recently played</h2>
          </div>
          <div className="space-y-2">
            {detail.recently_played.map((s) => (
              <RecentlyPlayedCard
                key={s.id}
                session={s}
                isAdmin={detail.role === "admin"}
                resultsVisible={resultsVisible}
                onRecord={() => s.format && onRecord(s.id, s.format)}
                onShowResults={() => onShowResults(s.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Ladder is shown to members only when standings are public; admins
          always see it (to manage). Hidden groups keep ratings silent. */}
      {ladderVisible && (
        <div id="sec-ladder" className="cs-reveal scroll-mt-28">
          <LadderSection
            groupId={detail.id}
            members={detail.members}
            isAdmin={detail.role === "admin"}
            defaultFormat={detail.default_format === "mixed" ? "doubles" : detail.default_format}
          />
        </div>
      )}

      <div id="sec-polls" className="scroll-mt-28">
        <PollsSection detail={detail} onAskAgent={onAskAgent} />
      </div>

      <div id="sec-discussion" className="scroll-mt-28">
        <DiscussionSection groupId={detail.id} onOpenChat={onOpenChat} />
      </div>

      <div id="sec-members" className="cs-reveal mb-16 scroll-mt-28">
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display font-bold text-2xl">
            Members <span className="text-ink/35 font-mono text-base">· {detail.members.length}</span>
          </h2>
          {detail.role === "admin" && (
            <button
              onClick={() => onAskAgent(`Send a reminder to everyone in ${detail.name}`)}
              className="font-mono text-[11px] uppercase tracking-wider text-ink/60 hover:text-ink"
            >
              Remind everyone →
            </button>
          )}
        </div>
        {memberError && <div className="text-sm text-[#C84B31] mb-2">{memberError}</div>}
        <div className="grid sm:grid-cols-2 gap-2">
          {detail.members.map((m) => (
            <MemberCard
              key={m.user_id}
              member={m}
              canRemove={detail.role === "admin" && !m.is_you && !(m.role === "admin" && adminCount <= 1)}
              busy={removingId === m.user_id}
              onRemove={() => handleRemoveMember(m)}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function PollsSection({
  detail,
  onAskAgent,
}: {
  detail: GroupDetailDTO;
  onAskAgent: (prefill?: string) => void;
}) {
  const [polls, setPolls] = useState<PollDTO[] | null>(null);
  const [creating, setCreating] = useState(false);

  // pollsAdminOnly defaults true; members can create only if explicitly false.
  const adminOnly = (detail.settings?.pollsAdminOnly ?? true) as boolean;
  const canCreate = detail.role === "admin" || !adminOnly;

  useEffect(() => {
    let cancelled = false;
    listPolls(detail.id)
      .then((r) => !cancelled && setPolls(r.polls))
      .catch(() => !cancelled && setPolls([]));
    return () => { cancelled = true; };
  }, [detail.id]);

  function replacePoll(p: PollDTO) {
    setPolls((cur) => (cur ?? []).map((x) => (x.id === p.id ? p : x)));
  }

  const openCount = (polls ?? []).filter((p) => p.status === "open").length;

  return (
    <div className="cs-reveal mb-12">
      <div className="flex items-end justify-between mb-3">
        <h2 className="font-display font-bold text-2xl">
          Polls{polls && polls.length > 0 && (
            <span className="text-ink/35 font-mono text-base"> · {openCount} open</span>
          )}
        </h2>
        {canCreate && (
          <button
            onClick={() => setCreating(true)}
            className="font-mono text-[11px] uppercase tracking-wider bg-accent px-3 py-1.5 cs-tap"
          >
            + New poll
          </button>
        )}
      </div>

      {polls === null ? (
        <div className="h-24 bg-ink/10 rounded-md" />
      ) : polls.length === 0 ? (
        <div className="border border-dashed border-ink/20 rounded-md p-6 text-center text-ink/55 text-sm">
          No polls yet.
          {canCreate ? (
            <button
              onClick={() => setCreating(true)}
              className="block mx-auto mt-3 px-4 py-2 bg-ink text-bg font-display font-bold text-sm cs-tap"
            >
              Create one
            </button>
          ) : (
            <div className="mt-1 text-ink/45">Admins can post polls for the club.</div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {polls.map((p) => (
            <PollCard key={p.id} poll={p} onChanged={replacePoll} />
          ))}
        </div>
      )}

      {creating && (
        <CreatePollModal
          groupId={detail.id}
          groupName={detail.name}
          onClose={() => setCreating(false)}
          onCreated={(p) => {
            setPolls((cur) => [p, ...(cur ?? [])]);
            setCreating(false);
          }}
          onAskAgent={onAskAgent}
        />
      )}
    </div>
  );
}

const CLOSE_OPTIONS: { label: string; hours: number | null }[] = [
  { label: "in 1 week", hours: 168 },
  { label: "in 3 days", hours: 72 },
  { label: "in 24 hours", hours: 24 },
  { label: "no deadline", hours: null },
];

function CreatePollModal({
  groupId,
  groupName,
  onClose,
  onCreated,
  onAskAgent,
}: {
  groupId: string;
  groupName: string;
  onClose: () => void;
  onCreated: (p: PollDTO) => void;
  onAskAgent: (prefill?: string) => void;
}) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState<string[]>(["", ""]);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [closeHours, setCloseHours] = useState<number | null>(168);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validOptions = options.map((o) => o.trim()).filter(Boolean);
  const canSubmit = question.trim().length > 0 && validOptions.length >= 2 && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const poll = await createPoll({
        group_id: groupId,
        question: question.trim(),
        options: validOptions,
        allow_multiple: allowMultiple,
        anonymous,
        closes_in_hours: closeHours,
      });
      onCreated(poll);
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-ink/55 cs-modal-overlay" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 cs-modal-card sm:inset-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:max-w-md sm:mx-auto">
        <div className="max-w-md mx-auto px-4 pb-4" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
          <div className="bg-bg border-2 border-ink rounded-t-xl sm:rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-ink text-bg flex items-center justify-between">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-accent" /> New poll · {groupName}
              </span>
              <button onClick={onClose} className="text-bg/60 hover:text-bg text-2xl leading-none" aria-label="Close">×</button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55 mb-1">Question</div>
                <input
                  type="text"
                  autoFocus
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="What time works best Saturday?"
                  className="w-full"
                />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55 mb-1">Options</div>
                <div className="space-y-2">
                  {options.map((o, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={o}
                        onChange={(e) => {
                          const next = [...options];
                          next[i] = e.target.value;
                          setOptions(next);
                        }}
                        placeholder={`Option ${i + 1}`}
                        className="flex-1"
                      />
                      {options.length > 2 && (
                        <button
                          onClick={() => setOptions(options.filter((_, j) => j !== i))}
                          className="text-ink/30 hover:text-warn text-lg px-1"
                          aria-label="Remove option"
                        >×</button>
                      )}
                    </div>
                  ))}
                </div>
                {options.length < 10 && (
                  <button
                    onClick={() => setOptions([...options, ""])}
                    className="mt-2 font-mono text-[11px] uppercase tracking-wider text-ink/60 hover:text-ink"
                  >+ Add option</button>
                )}
              </div>
              <div className="border-t border-line pt-4 space-y-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={allowMultiple} onChange={(e) => setAllowMultiple(e.target.checked)} />
                  Allow multiple choices
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
                  Anonymous (hide who voted)
                </label>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-ink/60">Closes:</span>
                  <select
                    value={closeHours === null ? "null" : String(closeHours)}
                    onChange={(e) => setCloseHours(e.target.value === "null" ? null : Number(e.target.value))}
                    className="border-2 border-ink/15 rounded px-2 py-1 font-mono text-xs bg-bg"
                  >
                    {CLOSE_OPTIONS.map((c) => (
                      <option key={c.label} value={c.hours === null ? "null" : String(c.hours)}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              {error && <div className="text-sm text-warn">{error}</div>}
              <div className="flex gap-2 pt-1">
                <button onClick={onClose} className="flex-1 border-2 border-ink/15 rounded-md py-2.5 font-display font-bold cs-tap">Cancel</button>
                <button onClick={submit} disabled={!canSubmit} className="flex-1 bg-accent rounded-md py-2.5 font-display font-bold cs-tap disabled:opacity-50">
                  {busy ? "Posting…" : "Post poll"}
                </button>
              </div>
              <button
                onClick={() => { onClose(); onAskAgent("Create a poll for the club"); }}
                className="w-full text-center font-mono text-[10px] uppercase tracking-wider text-ink/40 hover:text-ink/70"
              >
                or just ask ClubBot →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Admin overflow menu in the group header (consolidates Message + Settings).
function ManageMenu({
  onMessage,
  onSettings,
}: {
  onMessage: () => void;
  onSettings: () => void;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    function close(e: MouseEvent) {
      const t = e.target as HTMLElement | null;
      if (t?.closest("[data-manage-menu]")) return;
      setOpen(false);
    }
    function esc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("click", close);
    window.addEventListener("keydown", esc);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", esc);
    };
  }, [open]);
  return (
    <div className="relative shrink-0" data-manage-menu>
      <button
        onClick={() => setOpen((v) => !v)}
        className="font-mono text-[11px] uppercase tracking-wider bg-accent text-ink px-3 py-1.5 cs-tap flex items-center gap-1"
      >
        Manage <span className="text-[9px] leading-none">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 top-10 min-w-[190px] bg-bg border-2 border-ink rounded-md shadow-lg z-40 overflow-hidden cs-modal-card">
          <button
            onClick={() => { setOpen(false); onMessage(); }}
            className="w-full text-left px-4 py-3 font-display font-bold text-sm hover:bg-bg-2 cs-tap border-b border-ink/10"
          >
            📢 Message group
          </button>
          <button
            onClick={() => { setOpen(false); onSettings(); }}
            className="w-full text-left px-4 py-3 font-display font-bold text-sm hover:bg-bg-2 cs-tap"
          >
            ⚙ Settings
          </button>
        </div>
      )}
    </div>
  );
}

// Club-level merged feed: all of a group's game-chat messages, newest-first,
// each tagged with its game. Read-only — tap a message to open that game's
// thread and reply.
function DiscussionSection({
  groupId,
  onOpenChat,
}: {
  groupId: string;
  onOpenChat: (sessionId: string, title: string) => void;
}) {
  const [messages, setMessages] = useState<ClubMessageDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMessages(null);
    getGroupMessages(groupId)
      .then((m) => !cancelled && setMessages(m))
      .catch((e) => !cancelled && setError(String(e?.message ?? e)));
    return () => { cancelled = true; };
  }, [groupId]);

  return (
    <div className="cs-reveal mb-10">
      <h2 className="font-display font-bold text-2xl mb-3">Discussion</h2>
      {error && <div className="text-sm text-[#C84B31]">{error}</div>}
      {messages && messages.length === 0 && (
        <div className="border border-dashed border-ink/20 rounded-md p-6 text-center text-ink/55 text-sm">
          No chatter yet. Open a game and start a thread — or @ClubBot a question.
        </div>
      )}
      {messages && messages.length > 0 && (
        <div className="border border-ink/15 rounded-md divide-y divide-ink/10">
          {messages.map((m) => {
            const gameLabel = new Date(m.game.starts_at).toLocaleString(undefined, {
              weekday: "short",
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            });
            const msgTime = new Date(m.created_at).toLocaleString(undefined, {
              month: "short",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            });
            return (
              <button
                key={m.id}
                onClick={() => onOpenChat(m.game.session_id, gameLabel)}
                className="w-full text-left px-4 py-3 hover:bg-ink/[0.03] cs-tap"
              >
                {/* which game this message is from */}
                <div className="font-mono text-[10px] uppercase tracking-[0.14em] bg-ink/5 text-ink/60 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm mb-1.5">
                  🎾 {gameLabel}
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-display font-bold text-sm truncate">
                    {m.is_bot ? "ClubBot" : m.name}
                  </span>
                  <span className="font-mono text-[10px] text-ink/40 shrink-0">{msgTime}</span>
                </div>
                <div className="text-sm text-ink/70 mt-0.5 line-clamp-2">{m.body}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Admin compose: broadcast a message to the whole group (in-app + email).
function AnnounceModal({
  groupId,
  groupName,
  onClose,
}: {
  groupId: string;
  groupName: string;
  onClose: () => void;
}) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentMsg, setSentMsg] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function send() {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    setError(null);
    try {
      const r = await announceToGroup(groupId, text);
      setSentMsg(`Sent to ${r.sent} member${r.sent === 1 ? "" : "s"}.`);
      setBody("");
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setSending(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-ink/55 cs-modal-overlay" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 cs-modal-card sm:inset-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:max-w-md sm:mx-auto sm:my-0">
        <div className="max-w-md mx-auto px-4 pb-4 sm:pt-0" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
          <div className="bg-bg border-2 border-ink rounded-t-xl sm:rounded-xl shadow-2xl overflow-hidden">
            <div className="px-5 py-3 bg-ink text-bg flex items-center justify-between">
              <div className="font-mono text-[11px] uppercase tracking-[0.16em] truncate">
                Message {groupName}
              </div>
              <button onClick={onClose} className="text-bg/60 hover:text-bg font-mono text-2xl leading-none ml-3" aria-label="Close">×</button>
            </div>
            <div className="p-5">
              {sentMsg ? (
                <div className="flex flex-col items-center gap-4 py-4 text-center">
                  <div className="text-3xl">📨</div>
                  <div className="font-display font-bold text-lg">{sentMsg}</div>
                  <button
                    onClick={onClose}
                    className="px-5 py-2 bg-ink text-bg font-display font-bold text-sm cs-tap rounded-md"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-ink/55 mb-3">
                    Goes to every member — in the app and by email.
                  </p>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={4}
                    autoFocus
                    placeholder="e.g. Court changed to Ct 7 for Saturday. Pairings: Dave+Sam, Priya+Alex."
                    className="w-full resize-none rounded-md border border-ink/20 px-3 py-2 text-sm focus:outline-none focus:border-ink/50 bg-bg"
                  />
                  {error && <div className="text-sm text-[#C84B31] mt-2">{error}</div>}
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={() => void send()}
                      disabled={sending || !body.trim()}
                      className="px-4 py-2 bg-ink text-bg font-display font-bold text-sm cs-tap rounded-md disabled:opacity-40"
                    >
                      {sending ? "Sending…" : "Send to group"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function InviteLink({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/join/${code}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }
  return (
    <div className="cs-reveal mb-8">
      <h2 className="font-display font-bold text-xl tracking-tight">Invite players</h2>
      <p className="text-ink/55 text-sm mt-0.5 mb-3">
        This is how your club grows — share this link and anyone who taps it joins instantly.
      </p>
      <div className="border-2 border-ink rounded-md flex items-center overflow-hidden">
        <div className="flex-1 px-5 py-4 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55 mb-1">
            Invite link
          </div>
          <div className="font-mono text-sm sm:text-base truncate">{url}</div>
        </div>
        <button
          onClick={copy}
          className="bg-accent text-ink px-5 py-4 font-display font-bold text-sm border-l-2 border-ink cs-tap hover:brightness-105 whitespace-nowrap"
        >
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
}

function MemberCard({
  member,
  canRemove,
  busy,
  onRemove,
}: {
  member: GroupMemberDTO;
  canRemove?: boolean;
  busy?: boolean;
  onRemove?: () => void;
}) {
  return (
    <div className="flex items-center gap-3 border border-ink/15 px-4 py-3 rounded-md">
      <div
        className={
          "w-9 h-9 rounded-full flex items-center justify-center font-display font-bold text-sm shrink-0 " +
          (member.is_you ? "bg-accent text-ink" : "bg-ink text-bg")
        }
      >
        {member.initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-sm truncate">
          {member.name}
          {member.role === "admin" && (
            <span className="font-mono text-[9px] uppercase text-ink/50 ml-1 tracking-wider">
              admin{member.is_you ? " · you" : ""}
            </span>
          )}
        </div>
        <div className="text-xs text-ink/55 font-mono">
          {member.skill != null ? `${member.skill.toFixed(1)}` : "—"}
        </div>
      </div>
      {canRemove && (
        <button
          disabled={busy}
          onClick={onRemove}
          title="Remove from club"
          className="shrink-0 font-mono text-[10px] uppercase tracking-wider border border-[#C84B31]/40 text-[#C84B31] hover:bg-[#C84B31]/5 px-2 py-1 cs-tap disabled:opacity-30"
        >
          Remove
        </button>
      )}
    </div>
  );
}
